import { useEffectPlease } from "@features/utils";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { CameraContext } from "./frame";

interface DrawingCanvasProps {
  width: number;
  height: number;
  mode: "erase" | "edit";
  pencil: "brush" | "eraser" | "square" | "circle" | null | undefined;
  brushSize?: number;
  drawing?: DrawingPath[]; // External drawing paths to sync with
  onChange?: (svgElement: SVGSVGElement | null) => void;
  onCommit?: (drawing: DrawingPath[]) => void;
  refDrawingSVG?: (drawing_svg_ref: React.RefObject<SVGSVGElement>) => void; // Get the drawing canvas ref
}

export interface Point {
  x: number;
  y: number;
}

export interface DrawingPath {
  points: Point[];
  pencil: "brush" | "eraser" | "square" | "circle";
  size: number;
  id: string;
  type?: "path" | "square" | "circle" | "line";
}

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  width,
  height,
  pencil,
  mode = "erase",
  brushSize = 60,
  drawing: externalDrawing,
  onChange,
  onCommit,
  refDrawingSVG,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const editRedLayerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<DrawingPath | null>(null);
  const [paths, setPaths] = useState<DrawingPath[]>(externalDrawing || []);
  const [isLineMode, setIsLineMode] = useState(false);
  const [lineStartPoint, setLineStartPoint] = useState<Point | null>(null);
  const lineModeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMousePositionRef = useRef<Point | null>(null);
  const cameraContext = useContext(CameraContext);

  useEffectPlease(() => {
    if (refDrawingSVG) refDrawingSVG(svgRef);
  }, []);

  useEffectPlease(() => {
    if (!cameraContext) return;
    cameraContext.setContext({
      invert: () => {
        console.log("Inverting drawing canvas", { currentPaths: paths.length });
        // Create a full canvas rectangle as background, then add all existing paths as erasers
        const fullCanvasPath: DrawingPath = {
          points: [
            { x: 0, y: 0 },
            { x: width, y: height },
          ],
          pencil: "brush",
          size: 1,
          id: `invert-bg-${Date.now()}`,
          type: "square",
        };

        // Convert existing paths to erasers to punch holes in the full canvas
        const invertedPaths = paths.map(
          (path) =>
            ({
              ...path,
              pencil: path.pencil === "brush" ? "eraser" : "brush",
              id: `invert-${path.id}`,
            } as DrawingPath)
        );

        const newPaths = [fullCanvasPath, ...invertedPaths];
        console.log("Setting inverted paths", { newPaths: newPaths });
        setPaths(newPaths);
        if (onCommit) {
          onCommit(newPaths);
        }
      },
      reset: () => {
        setPaths([]);
        setCurrentPath(null);
        if (onCommit) {
          onCommit([]);
        }
      },
    });
  }, [paths, onCommit, cameraContext, width, height]);

  // Notify parent of SVG changes
  useEffect(() => {
    if (onChange && svgRef.current) {
      if (editRedLayerRef.current) {
        if (mode === "edit") {
          editRedLayerRef.current.style.maskImage = `url(data:image/svg+xml;base64,${btoa(
            new XMLSerializer().serializeToString(svgRef.current)
          )})`;
          editRedLayerRef.current.style.maskMode = "luminance";
        } else {
          editRedLayerRef.current.style.maskImage = "";
          editRedLayerRef.current.style.maskMode = "";
        }
      }
      onChange(svgRef.current);
    }
  }, [paths, currentPath, onChange, mode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (lineModeTimeoutRef.current) {
        clearTimeout(lineModeTimeoutRef.current);
      }
    };
  }, []);

  // Sync with external drawing prop
  useEffect(() => {
    setPaths(externalDrawing || []);
  }, [externalDrawing]);

  const compressPath = useCallback((points: Point[], epsilon = 2): Point[] => {
    if (points.length < 3) return points;

    // Perpendicular distance from p to segment ab
    const perpDist = (p: Point, a: Point, b: Point): number => {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      if (dx === 0 && dy === 0) return Math.hypot(p.x - a.x, p.y - a.y);
      const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
      const projX = a.x + t * dx;
      const projY = a.y + t * dy;
      return Math.hypot(p.x - projX, p.y - projY);
    };

    const rdp = (pts: Point[], start: number, end: number, out: Point[]) => {
      let maxDist = -1;
      let index = -1;

      for (let i = start + 1; i < end; i++) {
        const d = perpDist(pts[i], pts[start], pts[end]);
        if (d > maxDist) {
          maxDist = d;
          index = i;
        }
      }

      if (maxDist > epsilon && index !== -1) {
        rdp(pts, start, index, out);
        rdp(pts, index, end, out);
      } else {
        out.push(pts[start]);
      }
    };

    const out: Point[] = [];
    rdp(points, 0, points.length - 1, out);
    out.push(points[points.length - 1]);
    return out;
  }, []);

  // Helper function to round numbers to 2 decimal places
  const round = useCallback((num: number): number => {
    return Math.round(num * 100) / 100;
  }, []);

  // Helper function to check if a path is close to being a straight line
  const isPathCloseToLine = useCallback(
    (path: DrawingPath, threshold = 50): boolean => {
      if (!path || path.points.length < 3) return true; // Too few points, consider it a line

      const points = path.points;
      const start = points[0];
      const end = points[points.length - 1];

      // Calculate the distance between start and end points
      const lineLength = Math.sqrt(
        Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
      );

      // If the line is very short, don't consider it for line mode
      if (lineLength < 30) return false;

      // Calculate maximum deviation from the straight line
      let maxDeviation = 0;

      for (let i = 1; i < points.length - 1; i++) {
        const point = points[i];

        // Calculate perpendicular distance from point to line (start -> end)
        const A = end.y - start.y;
        const B = start.x - end.x;
        const C = end.x * start.y - start.x * end.y;

        const distance =
          Math.abs(A * point.x + B * point.y + C) / Math.sqrt(A * A + B * B);
        maxDeviation = Math.max(maxDeviation, distance);
      }

      // Path is close to a line if max deviation is below threshold
      return maxDeviation < threshold;
    },
    []
  );

  // Function to calculate painted area percentage using direct path rendering
  const calculatePaintedArea = useCallback(
    async (pathsToAnalyze: DrawingPath[]): Promise<number> => {
      if (pathsToAnalyze.length === 0) return 0;

      try {
        // Create a temporary canvas to render the paths
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return 0;

        // Use a smaller canvas for performance (scale down for analysis)
        const scale = 0.25; // Analyze at 25% resolution for performance
        const canvasWidth = Math.floor(width * scale);
        const canvasHeight = Math.floor(height * scale);

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        // Fill with black background
        ctx.fillStyle = "black";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Create SVG string with all paths using the same rendering logic
        const svgContent = `
          <svg width="${canvasWidth}" height="${canvasHeight}" xmlns="http://www.w3.org/2000/svg">
            <rect width="100%" height="100%" fill="black"/>
            ${pathsToAnalyze
              .map((path) => {
                const scaledPath = {
                  ...path,
                  points: path.points.map((p) => ({
                    x: p.x * scale,
                    y: p.y * scale,
                  })),
                  size: path.size * scale,
                };

                // Generate SVG path string inline
                let pathString = "";
                if (path.type === "square" && path.points.length >= 2) {
                  const start = path.points[0];
                  const end = path.points[path.points.length - 1];
                  const width = Math.abs(end.x - start.x) * scale;
                  const height = Math.abs(end.y - start.y) * scale;
                  const x = Math.min(start.x, end.x) * scale;
                  const y = Math.min(start.y, end.y) * scale;
                  pathString = `M ${x} ${y} L ${x + width} ${y} L ${
                    x + width
                  } ${y + height} L ${x} ${y + height} Z`;
                } else if (path.type === "circle" && path.points.length >= 2) {
                  const start = path.points[0];
                  const end = path.points[path.points.length - 1];
                  const centerX = ((start.x + end.x) / 2) * scale;
                  const centerY = ((start.y + end.y) / 2) * scale;
                  const radiusX = (Math.abs(end.x - start.x) / 2) * scale;
                  const radiusY = (Math.abs(end.y - start.y) / 2) * scale;
                  pathString = `M ${
                    centerX - radiusX
                  } ${centerY} A ${radiusX} ${radiusY} 0 1 1 ${
                    centerX + radiusX
                  } ${centerY} A ${radiusX} ${radiusY} 0 1 1 ${
                    centerX - radiusX
                  } ${centerY}`;
                } else {
                  // Regular path (brush/eraser)
                  if (scaledPath.points.length > 0) {
                    pathString = `M ${scaledPath.points[0].x} ${scaledPath.points[0].y}`;
                    for (let i = 1; i < scaledPath.points.length; i++) {
                      pathString += ` L ${scaledPath.points[i].x} ${scaledPath.points[i].y}`;
                    }
                  }
                }

                return `<path
                  d="${pathString}"
                  stroke="${
                    path.type === "path" || path.type === "line"
                      ? path.pencil === "brush"
                        ? "white"
                        : "black"
                      : "none"
                  }"
                  stroke-width="${
                    path.type === "path" || path.type === "line"
                      ? scaledPath.size
                      : 0
                  }"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="${
                    !(path.type === "path" || path.type === "line")
                      ? "white"
                      : "none"
                  }"
                />`;
              })
              .join("")}
          </svg>
        `;

        // Convert SVG to image and draw on canvas
        const img = new Image();
        const svgBlob = new Blob([svgContent], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);

        return new Promise((resolve) => {
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
            URL.revokeObjectURL(url);

            // Get image data and count non-black pixels
            const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
            const data = imageData.data;
            let paintedPixels = 0;

            // Sample every 4th pixel for performance
            const step = 4;
            for (let i = 0; i < data.length; i += 4 * step) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              const a = data[i + 3];

              // Consider pixel painted if it's not black (allowing for some tolerance)
              // and has some opacity
              if (a > 50 && (r > 15 || g > 15 || b > 15)) {
                paintedPixels += step; // Account for skipped pixels
              }
            }

            const totalPixels = canvasWidth * canvasHeight;
            const paintedPercentage = (paintedPixels / totalPixels) * 100;

            console.log(`Painted area: ${paintedPercentage.toFixed(2)}%`);
            resolve(Math.min(paintedPercentage, 100)); // Cap at 100%
          };

          img.onerror = () => {
            console.warn("Error loading SVG for painted area calculation");
            URL.revokeObjectURL(url);
            resolve(0);
          };

          img.src = url;
        });
      } catch (error) {
        console.warn("Error calculating painted area:", error);
        return 0;
      }
    },
    [width, height]
  );

  // Check if canvas should be reset due to low coverage
  const checkAndResetIfEmpty = useCallback(
    async (allPaths: DrawingPath[]) => {
      if (allPaths.length === 0) return;

      const paintedPercentage = await calculatePaintedArea(allPaths);
      console.log(`Canvas painted area: ${paintedPercentage.toFixed(2)}%`);

      // If less than 1% is painted, reset all paths
      if (paintedPercentage < 0.01) {
        console.log("Canvas is mostly empty, resetting all paths");
        setPaths([]);
        if (onCommit) {
          onCommit([]);
        }
      }
    },
    [calculatePaintedArea, onCommit]
  );

  // Get mouse/touch position relative to SVG
  const getPosition = useCallback(
    (event: MouseEvent | TouchEvent): Point => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };

      const rect = svg.getBoundingClientRect();
      const scaleX = width / rect.width;
      const scaleY = height / rect.height;

      if ("touches" in event) {
        const touch = event.touches[0] || event.changedTouches[0];
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
        };
      } else {
        return {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY,
        };
      }
    },
    [width, height]
  );

  // Convert path points to SVG path string
  const pathToSVGPath = useCallback(
    (drawingPath: DrawingPath): string => {
      const { points, type } = drawingPath;

      if (points.length === 0) return "";

      // Handle different shape types
      if (type === "square" && points.length >= 2) {
        const start = points[0];
        const end = points[points.length - 1];
        const width = Math.abs(end.x - start.x);
        const height = Math.abs(end.y - start.y);
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        return `M ${round(x)} ${round(y)} L ${round(x + width)} ${round(
          y
        )} L ${round(x + width)} ${round(y + height)} L ${round(x)} ${round(
          y + height
        )} Z`;
      }

      if (type === "circle" && points.length >= 2) {
        const start = points[0];
        const end = points[points.length - 1];
        const centerX = (start.x + end.x) / 2;
        const centerY = (start.y + end.y) / 2;
        const radiusX = Math.abs(end.x - start.x) / 2;
        const radiusY = Math.abs(end.y - start.y) / 2;
        return `M ${round(centerX - radiusX)} ${round(centerY)} A ${round(
          radiusX
        )} ${round(radiusY)} 0 1 1 ${round(centerX + radiusX)} ${round(
          centerY
        )} A ${round(radiusX)} ${round(radiusY)} 0 1 1 ${round(
          centerX - radiusX
        )} ${round(centerY)}`;
      }

      if (type === "line" && points.length >= 2) {
        const start = points[0];
        const end = points[points.length - 1];
        return `M ${round(start.x)} ${round(start.y)} L ${round(end.x)} ${round(
          end.y
        )}`;
      }

      // Default path handling (brush/eraser) - no single point handling
      // Always create a path, even with just one point by duplicating it
      const compressedPoints = compressPath(
        points.length === 1 ? [points[0], points[0]] : points
      );

      if (compressedPoints.length === 0) return "";

      let path = `M ${round(compressedPoints[0].x)} ${round(
        compressedPoints[0].y
      )}`;
      for (let i = 1; i < compressedPoints.length; i++) {
        path += ` L ${round(compressedPoints[i].x)} ${round(
          compressedPoints[i].y
        )}`;
      }
      return path;
    },
    [compressPath, round]
  );

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!pencil) return;

      event.preventDefault();
      setIsDrawing(true);
      const pos = getPosition(event.nativeEvent);
      lastMousePositionRef.current = pos;

      // For shapes, create path immediately since they need visual feedback
      if (pencil === "square" || pencil === "circle") {
        const newPath: DrawingPath = {
          points: [pos],
          pencil: "brush",
          size: brushSize,
          id: `path-${Date.now()}-${Math.random()}`,
          type: pencil,
        };
        setCurrentPath(newPath);
      } else {
        // For brush/eraser, don't create path until movement or timeout
        setCurrentPath(null);
      }

      // Set up line mode timeout for brush/eraser tools
      if (pencil === "brush" || pencil === "eraser") {
        setIsLineMode(false);
        setLineStartPoint(pos);

        lineModeTimeoutRef.current = setTimeout(() => {
          // Only trigger line mode if current path is close to being a line
          setCurrentPath((prev) => {
            const linePoints = [pos, lastMousePositionRef.current || pos];
            const testPath: DrawingPath = {
              points: prev?.points || linePoints,
              pencil: pencil === "eraser" ? "eraser" : "brush",
              size: brushSize,
              id: "test",
              type: "path",
            };

            if (isPathCloseToLine(testPath)) {
              setIsLineMode(true);
              if (prev) {
                return {
                  ...prev,
                  points: linePoints,
                  type: "line",
                };
              } else {
                return {
                  points: linePoints,
                  pencil: pencil === "eraser" ? "eraser" : "brush",
                  size: brushSize,
                  id: `path-${Date.now()}-${Math.random()}`,
                  type: "line",
                };
              }
            }
            return prev; // Don't switch to line mode if path isn't line-like
          });
        }, 2000);
      }
    },
    [pencil, getPosition, brushSize, isPathCloseToLine]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent | React.MouseEvent) => {
      if (!isDrawing || !pencil) return;

      event.preventDefault();
      const pos = getPosition(event as MouseEvent);

      // If no current path exists (brush/eraser), create it on first movement
      if (!currentPath && (pencil === "brush" || pencil === "eraser")) {
        const startPos = lineStartPoint || lastMousePositionRef.current || pos;
        const newPath: DrawingPath = {
          points: [startPos, pos],
          pencil: pencil === "eraser" ? "eraser" : "brush",
          size: brushSize,
          id: `path-${Date.now()}-${Math.random()}`,
          type: "path",
        };
        setCurrentPath(newPath);
        lastMousePositionRef.current = pos;
        return;
      }

      if (!currentPath) return;

      // Check if mouse moved (for line mode detection)
      const lastPos = lastMousePositionRef.current;
      if (lastPos && (pencil === "brush" || pencil === "eraser")) {
        const distance = Math.sqrt(
          Math.pow(pos.x - lastPos.x, 2) + Math.pow(pos.y - lastPos.y, 2)
        );
        if (distance > 5) {
          // Threshold for movement detection
          // Mouse moved, reset line mode timeout
          if (lineModeTimeoutRef.current) {
            clearTimeout(lineModeTimeoutRef.current);
            lineModeTimeoutRef.current = setTimeout(() => {
              // Only trigger line mode if current path is close to being a line
              setCurrentPath((prev) => {
                if (prev && prev.points.length > 0) {
                  if (isPathCloseToLine(prev)) {
                    setIsLineMode(true);
                    return {
                      ...prev,
                      points: [prev.points[0], pos],
                      type: "line",
                    };
                  }
                }
                return prev; // Don't switch to line mode if path isn't line-like
              });
            }, 2000);
          }
        }
      }
      lastMousePositionRef.current = pos;

      setCurrentPath((prev) => {
        if (!prev) return null;

        // Handle different drawing modes
        if (prev.type === "square" || prev.type === "circle") {
          // For shapes, only update the end point
          return {
            ...prev,
            points: [prev.points[0], pos],
          };
        } else if (isLineMode && lineStartPoint) {
          // Line mode: only track start and current position
          return {
            ...prev,
            points: [lineStartPoint, pos],
            type: "line",
          };
        } else {
          // Normal path mode: add all points
          return {
            ...prev,
            points: [...prev.points, pos],
          };
        }
      });
    },
    [
      isDrawing,
      pencil,
      currentPath,
      getPosition,
      isLineMode,
      lineStartPoint,
      brushSize,
      isPathCloseToLine,
    ]
  );

  const handleMouseUp = useCallback(async () => {
    // Clear line mode timeout
    if (lineModeTimeoutRef.current) {
      clearTimeout(lineModeTimeoutRef.current);
      lineModeTimeoutRef.current = null;
    }

    // Reset line mode state
    setIsLineMode(false);
    setLineStartPoint(null);
    lastMousePositionRef.current = null;

    if (currentPath) {
      const newPaths = [...paths, currentPath];
      setPaths(newPaths);
      setCurrentPath(null);

      // Call onCommit with the complete drawing
      if (onCommit) {
        onCommit(newPaths);
      }

      // Check if canvas should be reset due to low coverage immediately
      checkAndResetIfEmpty(newPaths);
    }
    setIsDrawing(false);
  }, [currentPath, paths, onCommit, checkAndResetIfEmpty]);

  // Attach global mouse events when drawing to track movement outside canvas
  useEffect(() => {
    if (isDrawing) {
      const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
      const handleGlobalMouseUp = () => handleMouseUp();

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDrawing, handleMouseMove, handleMouseUp]);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (!pencil) return;

      event.preventDefault();
      setIsDrawing(true);
      const pos = getPosition(event.nativeEvent);
      lastMousePositionRef.current = pos;

      // For shapes, create path immediately since they need visual feedback
      if (pencil === "square" || pencil === "circle") {
        const newPath: DrawingPath = {
          points: [pos],
          pencil: "brush",
          size: brushSize,
          id: `path-${Date.now()}-${Math.random()}`,
          type: pencil,
        };
        setCurrentPath(newPath);
      } else {
        // For brush/eraser, don't create path until movement or timeout
        setCurrentPath(null);
      }

      // Set up line mode timeout for brush/eraser tools
      if (pencil === "brush" || pencil === "eraser") {
        setIsLineMode(false);
        setLineStartPoint(pos);

        lineModeTimeoutRef.current = setTimeout(() => {
          // Only trigger line mode if current path is close to being a line
          setCurrentPath((prev) => {
            const linePoints = [pos, lastMousePositionRef.current || pos];
            const testPath: DrawingPath = {
              points: prev?.points || linePoints,
              pencil: pencil === "eraser" ? "eraser" : "brush",
              size: brushSize,
              id: "test",
              type: "path",
            };

            if (isPathCloseToLine(testPath)) {
              setIsLineMode(true);
              if (prev) {
                return {
                  ...prev,
                  points: linePoints,
                  type: "line",
                };
              } else {
                return {
                  points: linePoints,
                  pencil: pencil === "eraser" ? "eraser" : "brush",
                  size: brushSize,
                  id: `path-${Date.now()}-${Math.random()}`,
                  type: "line",
                };
              }
            }
            return prev; // Don't switch to line mode if path isn't line-like
          });
        }, 2000);
      }
    },
    [pencil, getPosition, brushSize, isPathCloseToLine]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!isDrawing || !pencil) return;

      event.preventDefault();
      const pos = getPosition(event.nativeEvent);

      // If no current path exists (brush/eraser), create it on first movement
      if (!currentPath && (pencil === "brush" || pencil === "eraser")) {
        const startPos = lineStartPoint || lastMousePositionRef.current || pos;
        const newPath: DrawingPath = {
          points: [startPos, pos],
          pencil: pencil === "eraser" ? "eraser" : "brush",
          size: brushSize,
          id: `path-${Date.now()}-${Math.random()}`,
          type: "path",
        };
        setCurrentPath(newPath);
        lastMousePositionRef.current = pos;
        return;
      }

      if (!currentPath) return;

      // Check if touch moved (for line mode detection)
      const lastPos = lastMousePositionRef.current;
      if (lastPos && (pencil === "brush" || pencil === "eraser")) {
        const distance = Math.sqrt(
          Math.pow(pos.x - lastPos.x, 2) + Math.pow(pos.y - lastPos.y, 2)
        );
        if (distance > 5) {
          // Threshold for movement detection
          // Touch moved, reset line mode timeout
          if (lineModeTimeoutRef.current) {
            clearTimeout(lineModeTimeoutRef.current);
            lineModeTimeoutRef.current = setTimeout(() => {
              // Only trigger line mode if current path is close to being a line
              setCurrentPath((prev) => {
                if (prev && prev.points.length > 0) {
                  if (isPathCloseToLine(prev)) {
                    setIsLineMode(true);
                    return {
                      ...prev,
                      points: [prev.points[0], pos],
                      type: "line",
                    };
                  }
                }
                return prev; // Don't switch to line mode if path isn't line-like
              });
            }, 2000);
          }
        }
      }
      lastMousePositionRef.current = pos;

      setCurrentPath((prev) => {
        if (!prev) return null;

        // Handle different drawing modes
        if (prev.type === "square" || prev.type === "circle") {
          // For shapes, only update the end point
          return {
            ...prev,
            points: [prev.points[0], pos],
          };
        } else if (isLineMode && lineStartPoint) {
          // Line mode: only track start and current position
          return {
            ...prev,
            points: [lineStartPoint, pos],
            type: "line",
          };
        } else {
          // Normal path mode: add all points
          return {
            ...prev,
            points: [...prev.points, pos],
          };
        }
      });
    },
    [
      isDrawing,
      pencil,
      currentPath,
      getPosition,
      isLineMode,
      lineStartPoint,
      brushSize,
      isPathCloseToLine,
    ]
  );

  const handleTouchEnd = useCallback(async () => {
    // Clear line mode timeout
    if (lineModeTimeoutRef.current) {
      clearTimeout(lineModeTimeoutRef.current);
      lineModeTimeoutRef.current = null;
    }

    // Reset line mode state
    setIsLineMode(false);
    setLineStartPoint(null);
    lastMousePositionRef.current = null;

    if (currentPath) {
      const newPaths = [...paths, currentPath];
      setPaths(newPaths);
      setCurrentPath(null);

      // Call onCommit with the complete drawing
      if (onCommit) {
        onCommit(newPaths);
      }

      // Check if canvas should be reset due to low coverage immediately
      checkAndResetIfEmpty(newPaths);
    }
    setIsDrawing(false);
  }, [currentPath, paths, onCommit, checkAndResetIfEmpty]);

  // Render all paths as SVG elements
  const allPaths = currentPath ? [...paths, currentPath] : paths;

  return (
    <div
      className="absolute inset-0 z-20"
      style={{ pointerEvents: pencil ? "auto" : "none" }}
    >
      {mode === "edit" && (
        <div
          ref={editRedLayerRef}
          className="absolute inset-0 w-full h-full bg-red-500 opacity-50"
        />
      )}
      {/* SVG for visual representation */}
      <svg
        ref={svgRef}
        xmlns="http://www.w3.org/2000/svg"
        className={twMerge("absolute inset-0 w-full h-full opacity-0")}
        style={{
          cursor: "default",
          touchAction: "none",
          mixBlendMode: "luminosity",
          background: "black",
        }}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {allPaths.map((path) => (
          <path
            key={path.id}
            d={pathToSVGPath(path)}
            data-pencil={path.pencil}
            stroke={
              // Only stroke for brush paths and erasers
              path.type === "path" || path.type === "line"
                ? path.pencil === "brush"
                  ? "white"
                  : "black"
                : "none"
            }
            strokeWidth={
              path.type === "path" || path.type === "line" ? path.size : 0
            }
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={
              // Fill for shapes, none for brush strokes and erasers
              !(path.type === "path" || path.type === "line")
                ? path.pencil === "brush"
                  ? "white"
                  : "black"
                : "none"
            }
            style={{
              pointerEvents: "none",
            }}
          />
        ))}
      </svg>
    </div>
  );
};

// Hook to use the drawing canvas
export const useDrawingCanvas = () => {
  const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);
  const [drawing, setDrawing] = useState<DrawingPath[]>([]);

  const handleSVGChange = useCallback((svg: SVGSVGElement | null) => {
    setSvgElement(svg);
  }, []);

  const handleDrawingChange = useCallback((newDrawing: DrawingPath[]) => {
    setDrawing(newDrawing);
  }, []);

  const getSVGDataURL = useCallback(() => {
    if (!svgElement) return null;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    return `data:image/svg+xml;base64,${btoa(svgString)}`;
  }, [svgElement]);

  const cloneSVG = useCallback(() => {
    return svgElement?.cloneNode(true) as SVGSVGElement | null;
  }, [svgElement]);

  return {
    handleSVGChange,
    handleDrawingChange,
    getSVGDataURL,
    cloneSVG,
    svgElement,
    drawing,
  };
};
